/**
 * LicenseRenewalPage — full-page renewal wizard (iter-278, Feb 2026)
 *
 * Route: /manage/cloudgm-agent/companies/:companyId/renew-license
 *        (also handles /manage/cloudgm-agent/companies/:companyId/renewals/:renewalId
 *         for view/edit of an existing renewal)
 *
 * Delivers points A + B + C of the Feb 2026 owner spec:
 *   • Reads renewals-meta to show "This will be your Nth renewal"
 *   • Multi-file renewal receipts (each with paid_to = to_gov | to_us)
 *   • Commission audit merged card:
 *       – Toggle "هل يوجد عمولة على تجديد الرخصة؟"
 *       – Toggle "هل يوجد عمولة للوكيل؟"
 *       – Commission receipts (each with paid_to tag)
 *       – Agent commission block with "Stop granting" button
 *   • Admin fees toggle with its own paid_to
 *   • AI verify all receipts against the declared renewal amount BEFORE save
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Trash2, Loader2, Upload, FileImage, Save, ArrowLeft, Sparkles,
  Info, CheckCircle2, History, Landmark, Building2, ShieldCheck, User,
  Coins, IdCard, Paperclip, AlertTriangle, Download, FileText,
} from 'lucide-react';

import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useLanguage } from '../context/LanguageContext';
import { getAuthHeaders } from '../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const emptyReceipt = () => ({
  file_id: '',
  filename: '',
  amount: '',
  paid_to: 'to_us',
  reference: '',
  ai_verified: false,
});

export default function LicenseRenewalPage() {
  const { companyId, renewalId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const isEdit = !!renewalId;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [existing, setExisting] = useState(null);   // when isEdit
  // iter-279c — Company snapshot for the "linked agent" banner (Task D).
  const [company, setCompany] = useState(null);

  const [licenseNumber, setLicenseNumber] = useState('');
  const [unifiedLicenseNumber, setUnifiedLicenseNumber] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [notes, setNotes] = useState('');
  // iter-280 (Feb 2026) — Extended license fields mirroring the Company
  // Registration TradeLicenseBox so the renewal form captures the full
  // trade-license snapshot rather than just the license number/dates.
  const [companyNameAr, setCompanyNameAr] = useState('');
  const [companyNameEn, setCompanyNameEn] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');
  const [licenseType, setLicenseType] = useState('');
  const [legalForm, setLegalForm] = useState('');
  const [capital, setCapital] = useState('');
  // Extra attachments (not receipts) — copies of the renewed license file,
  // approvals, etc. Simple data-URL storage, same pattern as
  // company-registration/AttachmentsBox.
  const [attachments, setAttachments] = useState([]);
  const attachRef = useRef(null);

  // Renewal receipts (paid_to per file)
  const [renewalReceipts, setRenewalReceipts] = useState([]);
  const [renewalVerifyResult, setRenewalVerifyResult] = useState(null);

  // Commission toggles + data
  const [hasRenewalCommission, setHasRenewalCommission] = useState(false);
  const [commissionReceipts, setCommissionReceipts] = useState([]);
  const [commissionVerifyResult, setCommissionVerifyResult] = useState(null);

  const [hasAgentCommission, setHasAgentCommission] = useState(false);
  const [agentCommissionAmount, setAgentCommissionAmount] = useState('');

  // Admin fees
  const [adminFeesApply, setAdminFeesApply] = useState(false);
  const [adminFeesAmount, setAdminFeesAmount] = useState('');
  const [adminFeesPaidTo, setAdminFeesPaidTo] = useState('to_us');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, c] = await Promise.all([
        axios.get(`${API}/admin/agent-companies/${companyId}/renewals-meta`,
                  { headers: getAuthHeaders() }),
        axios.get(`${API}/agent/companies/${companyId}`,
                  { headers: getAuthHeaders() }).catch(() => ({ data: null })),
      ]);
      setMeta(m.data);
      const companyDoc = c.data?.data || c.data || null;
      setCompany(companyDoc);
      if (isEdit) {
        const r = await axios.get(`${API}/admin/agent-companies/${companyId}/renewals/${renewalId}`,
                                   { headers: getAuthHeaders() });
        const d = r.data || {};
        setExisting(d);
        setLicenseNumber(d.license_number || '');
        setUnifiedLicenseNumber(d.unified_license_number || '');
        setIssueDate(d.issue_date || '');
        setExpiryDate(d.expiry_date || '');
        setNotes(d.notes || '');
        setCompanyNameAr(d.company_name_ar || companyDoc?.company_name_ar || '');
        setCompanyNameEn(d.company_name_en || companyDoc?.company_name_en || '');
        setIssuingAuthority(d.issuing_authority || companyDoc?.issuing_authority || '');
        setLicenseType(d.license_type || companyDoc?.license_type || '');
        setLegalForm(d.legal_form || companyDoc?.legal_form || '');
        setCapital(String(d.capital || companyDoc?.capital || ''));
        setAttachments(Array.isArray(d.attachments) ? d.attachments : []);
        setRenewalReceipts((d.renewal_receipts || []).map(r => ({
          ...r, ai_verified: r.ai_verified === true,
        })));
        setHasRenewalCommission(!!d.has_renewal_commission);
        setCommissionReceipts((d.commission_receipts || []).map(r => ({
          ...r, ai_verified: r.ai_verified === true,
        })));
        setHasAgentCommission(!!d.has_agent_commission);
        setAgentCommissionAmount(String(d.agent_commission_amount || ''));
        setAdminFeesApply(!!d.admin_fees_apply);
        setAdminFeesAmount(String(d.admin_fees_amount || ''));
        setAdminFeesPaidTo(d.admin_fees_paid_to || 'to_us');
      } else {
        // iter-280 — Auto-prefill company/license metadata from the
        // current license so the operator only edits what actually
        // changed (usually just the license number + dates).
        const cur = m.data?.current_license || {};
        setLicenseNumber('');   // fresh number expected — leave blank
        setUnifiedLicenseNumber(cur.unified_license_number || '');
        setIssueDate('');       // new issue date expected
        setExpiryDate('');      // new expiry date expected
        setCompanyNameAr(cur.company_name_ar || '');
        setCompanyNameEn(cur.company_name_en || '');
        setIssuingAuthority(cur.issuing_authority || '');
        setLicenseType(cur.license_type || '');
        setLegalForm(cur.legal_form || '');
        setCapital(String(cur.capital || ''));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, renewalId, isEdit]);

  useEffect(() => { load(); }, [load]);

  const renewalSum = useMemo(
    () => renewalReceipts.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [renewalReceipts]
  );
  const commissionSum = useMemo(
    () => commissionReceipts.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    [commissionReceipts]
  );

  const allRenewalsVerified = renewalReceipts.length > 0
    && renewalReceipts.every(r => r.file_id && parseFloat(r.amount) > 0 && r.ai_verified);
  const allCommissionsVerified = !hasRenewalCommission
    || (commissionReceipts.length > 0
        && commissionReceipts.every(r => r.file_id && parseFloat(r.amount) > 0 && r.ai_verified));

  const canSave =
    !!expiryDate &&
    !!licenseNumber &&
    !!issueDate &&
    renewalReceipts.length > 0 &&
    allRenewalsVerified &&
    allCommissionsVerified &&
    (!adminFeesApply || (parseFloat(adminFeesAmount) > 0 && adminFeesPaidTo)) &&
    (!hasAgentCommission || parseFloat(agentCommissionAmount) > 0);

  const upload = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const r = await axios.post(`${API}/upload/file`, fd, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
    });
    return r.data || {};
  };

  // iter-280 — Convert a File to a data URL for extra attachments.
  const fileToDataUrl = (f) => new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsDataURL(f);
  });

  const addAttachments = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const next = [...attachments];
    for (const f of list) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(t(`الملف ${f.name} أكبر من 10MB`, `${f.name} exceeds 10MB`));
        continue;
      }
      const data_url = await fileToDataUrl(f);
      next.push({
        name: f.name,
        label: f.name,
        mime_type: f.type,
        size: f.size,
        data_url,
      });
    }
    setAttachments(next);
    if (attachRef.current) attachRef.current.value = '';
    toast.success(t('تمت إضافة المرفقات', 'Attachments added'));
  };

  const addReceipts = async (files, kind) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const setter = kind === 'renewal' ? setRenewalReceipts : setCommissionReceipts;
    const clearResult = kind === 'renewal' ? setRenewalVerifyResult : setCommissionVerifyResult;
    const setLoad = (v) => clearResult(null);
    setLoad(null);
    const currentList = kind === 'renewal' ? renewalReceipts : commissionReceipts;
    const nextList = [...currentList];
    try {
      for (const file of list) {
        try {
          const rec = await upload(file);
          const file_id = rec.file_id || rec.id;
          let amount = '';
          let reference = '';
          if ((file.type || '').startsWith('image/')) {
            try {
              const ocr = await axios.post(`${API}/admin/invoices/ocr-receipt`,
                { file_id },
                { headers: getAuthHeaders() });
              const d = ocr.data || {};
              if (d.amount) amount = String(d.amount);
              if (d.reference) reference = String(d.reference);
            } catch { /* best-effort */ }
          }
          nextList.push({
            file_id,
            filename: rec.filename || file.name,
            amount,
            paid_to: 'to_us',
            reference,
            ai_verified: false,
          });
        } catch (e) {
          toast.error(e.response?.data?.detail || t(`فشل رفع ${file.name}`, `Failed to upload ${file.name}`));
        }
      }
      setter(nextList);
      toast.success(t('تم رفع الإيصالات', 'Receipts uploaded'));
    } catch (e) {
      toast.error(e.message);
    }
  };

  const removeReceipt = (kind, idx) => {
    if (kind === 'renewal') {
      setRenewalReceipts(rs => rs.filter((_, i) => i !== idx));
      setRenewalVerifyResult(null);
    } else {
      setCommissionReceipts(rs => rs.filter((_, i) => i !== idx));
      setCommissionVerifyResult(null);
    }
  };

  const updateReceipt = (kind, idx, patch) => {
    if (kind === 'renewal') {
      setRenewalReceipts(rs => rs.map((r, i) => i === idx ? { ...r, ...patch, ai_verified: false } : r));
      setRenewalVerifyResult(null);
    } else {
      setCommissionReceipts(rs => rs.map((r, i) => i === idx ? { ...r, ...patch, ai_verified: false } : r));
      setCommissionVerifyResult(null);
    }
  };

  const verifyReceipts = async (kind) => {
    const list = kind === 'renewal' ? renewalReceipts : commissionReceipts;
    const sum = kind === 'renewal' ? renewalSum : commissionSum;
    if (list.length === 0) return;
    if (list.some(r => !r.file_id || !parseFloat(r.amount))) {
      toast.error(t('أدخل مبلغاً لكل إيصال', 'Enter an amount for each receipt'));
      return;
    }
    try {
      const r = await axios.post(
        `${API}/admin/invoices/verify-receipts`,
        {
          file_ids: list.map(x => x.file_id),
          declared_total: sum,
        },
        { headers: getAuthHeaders() }
      );
      const d = r.data || {};
      if (kind === 'renewal') setRenewalVerifyResult(d);
      else setCommissionVerifyResult(d);
      if (d.ok) {
        if (kind === 'renewal') {
          setRenewalReceipts(rs => rs.map(x => ({ ...x, ai_verified: true })));
        } else {
          setCommissionReceipts(rs => rs.map(x => ({ ...x, ai_verified: true })));
        }
        toast.success(isAr ? d.reason_ar : d.reason_en);
      } else {
        toast.error(isAr ? d.reason_ar : d.reason_en);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('فشل التحقق', 'Verification failed'));
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body = {
        license_number: licenseNumber,
        unified_license_number: unifiedLicenseNumber,
        issue_date: issueDate,
        expiry_date: expiryDate,
        // iter-280 — trade-license snapshot fields
        company_name_ar: companyNameAr,
        company_name_en: companyNameEn,
        issuing_authority: issuingAuthority,
        license_type: licenseType,
        legal_form: legalForm,
        capital: capital ? parseFloat(capital) || 0 : 0,
        attachments,
        renewal_receipts: renewalReceipts.map(r => ({
          file_id: r.file_id,
          filename: r.filename,
          amount: parseFloat(r.amount),
          paid_to: r.paid_to || 'to_us',
          reference: r.reference || null,
          ai_verified: !!r.ai_verified,
        })),
        has_renewal_commission: hasRenewalCommission,
        commission_receipts: hasRenewalCommission ? commissionReceipts.map(r => ({
          file_id: r.file_id,
          filename: r.filename,
          amount: parseFloat(r.amount),
          paid_to: r.paid_to || 'to_us',
          reference: r.reference || null,
          ai_verified: !!r.ai_verified,
        })) : [],
        has_agent_commission: hasAgentCommission,
        agent_commission_amount: hasAgentCommission ? parseFloat(agentCommissionAmount) || 0 : 0,
        admin_fees_apply: adminFeesApply,
        admin_fees_amount: adminFeesApply ? parseFloat(adminFeesAmount) || 0 : 0,
        admin_fees_paid_to: adminFeesApply ? adminFeesPaidTo : null,
        notes,
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/agent-companies/${companyId}/renewals/${renewalId}`,
                          body, { headers: getAuthHeaders() });
        toast.success(t('تم الحفظ', 'Saved'));
      } else {
        await axios.post(`${API}/admin/agent-companies/${companyId}/renewals`,
                         body, { headers: getAuthHeaders() });
        toast.success(t('تم إنشاء طلب التجديد', 'Renewal request created'));
      }
      navigate(`/manage/cloudgm-agent/companies/${companyId}`);
    } catch (e) {
      const raw = e.response?.data?.detail;
      let msg;
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      else msg = t('فشل الحفظ', 'Save failed');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const stopAgentCommission = async () => {
    if (!isEdit) return;
    if (!window.confirm(t('إيقاف منح العمولة لهذا الوكيل؟ سيبقى السجل التاريخي محفوظاً.',
                          'Stop granting commission to this agent? The historical record will be preserved.'))) return;
    try {
      await axios.patch(`${API}/admin/agent-companies/${companyId}/renewals/${renewalId}`,
                        { stop_agent_commission: true },
                        { headers: getAuthHeaders() });
      toast.success(t('تم إيقاف العمولة', 'Commission stopped'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل', 'Failed'));
    }
  };

  const markAudited = async () => {
    if (!isEdit) return;
    try {
      await axios.post(`${API}/admin/agent-companies/${companyId}/renewals/${renewalId}/mark-audited`,
                        {}, { headers: getAuthHeaders() });
      toast.success(t('تم التدقيق', 'Marked as audited'));
      load();
    } catch (e) {
      const raw = e.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : t('فشل التدقيق', 'Audit failed'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030303] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-white/40" />
      </div>
    );
  }

  const renewalIndex = isEdit ? existing?.renewal_index : meta?.next_renewal_index;

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath={`/manage/cloudgm-agent/companies/${companyId}`} />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'وكيل CloudGM', label_en: 'CloudGM Agent', path: '/manage/cloudgm-agent' },
            { label_ar: 'الشركات', label_en: 'Companies', path: '/manage/cloudgm-agent/companies' },
            { label_ar: 'ملف الشركة', label_en: 'Company File', path: `/manage/cloudgm-agent/companies/${companyId}` },
            { label_ar: isEdit ? 'تفاصيل التجديد' : 'تجديد الرخصة التجارية',
              label_en: isEdit ? 'Renewal Details' : 'Renew Commercial License', path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-2">
              <History className="w-7 h-7 text-emerald-400" />
              {t('تجديد الرخصة التجارية', 'Renew Commercial License')}
            </h1>
            <p className="text-white/50 text-sm mt-2">
              {t(`التجديد رقم ${renewalIndex || '?'}${meta?.license_history_count ? ` — عدد التجديدات السابقة: ${meta.license_history_count}` : ''}`,
                 `Renewal #${renewalIndex || '?'}${meta?.license_history_count ? ` — Previous renewals: ${meta.license_history_count}` : ''}`)}
            </p>
            {existing?.audited && (
              <div className="mt-2 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 text-xs">
                <ShieldCheck className="w-3 h-3" />
                {t('تم التدقيق', 'Audited')}
              </div>
            )}
          </div>
          {isEdit && !existing?.audited && (
            <Button
              onClick={markAudited}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="renewal-mark-audited-btn"
            >
              <ShieldCheck className="w-4 h-4 me-2" />
              {t('تدقيق التجديد', 'Mark as audited')}
            </Button>
          )}
        </div>

        {/* iter-280 (Feb 2026) — Current License snapshot card.
             Shows the admin what license is being renewed at a glance
             (visible on both new-renewal and edit routes). Mirrors the
             comprehensive Trade-License card on the CompanyRegistration
             detail page. */}
        {meta?.current_license && (meta.current_license.license_number || meta.current_license.company_name_ar) && (
          <section
            className="rounded-2xl bg-gradient-to-br from-emerald-500/[0.08] to-emerald-500/[0.02] border border-emerald-500/25 p-5"
            data-testid="renewal-current-license-card"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="h-9 w-9 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <IdCard className="w-4 h-4 text-emerald-300" />
              </div>
              <div>
                <div className="text-xs text-emerald-300/80 font-semibold">
                  {t('الرخصة الحالية (سيتم تجديدها)', 'Current License (being renewed)')}
                </div>
                <div className="text-white/60 text-[10px]">
                  {t('البيانات المحفوظة على ملف الشركة حالياً', 'Data currently saved on the company file')}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <SnapshotField label={t('رقم الرخصة', 'License #')}
                             value={meta.current_license.license_number} mono />
              <SnapshotField label={t('الرقم الموحد', 'Unified #')}
                             value={meta.current_license.unified_license_number} mono />
              <SnapshotField label={t('تاريخ الإصدار', 'Issue date')}
                             value={meta.current_license.issue_date} mono />
              <SnapshotField label={t('تاريخ الانتهاء', 'Expiry date')}
                             value={meta.current_license.expiry_date} mono highlight />
              <SnapshotField label={t('اسم الشركة (عربي)', 'Company name (AR)')}
                             value={meta.current_license.company_name_ar} colSpan={2} />
              <SnapshotField label={t('اسم الشركة (إنجليزي)', 'Company name (EN)')}
                             value={meta.current_license.company_name_en} colSpan={2} />
              <SnapshotField label={t('جهة الإصدار', 'Authority')}
                             value={meta.current_license.issuing_authority} />
              <SnapshotField label={t('نوع الرخصة', 'License type')}
                             value={meta.current_license.license_type} />
              <SnapshotField label={t('الشكل القانوني', 'Legal form')}
                             value={meta.current_license.legal_form} />
              <SnapshotField label={t('رأس المال', 'Capital')}
                             value={meta.current_license.capital} mono />
            </div>
            {meta.current_license.license_file && (
              <div className="mt-3 pt-3 border-t border-emerald-500/20">
                <a
                  href={meta.current_license.license_file}
                  download={meta.current_license.license_file_name || 'current-license.pdf'}
                  className="inline-flex items-center gap-2 text-emerald-300 hover:text-emerald-200 text-xs"
                  data-testid="renewal-current-license-download"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t('تحميل صورة الرخصة الحالية', 'Download current license file')}
                </a>
              </div>
            )}
          </section>
        )}

        {/* iter-280 — First-renewal agent commission alert. When this is
             the customer's FIRST renewal (renewal_index === 1) and there
             is an agent linked to the file, remind the operator that agent
             commission is normally granted on first renewals per policy. */}
        {!isEdit && renewalIndex === 1 && company?.agent_name && (
          <section
            className="p-4 rounded-2xl bg-amber-500/[0.08] border border-amber-500/30 flex items-start gap-3"
            data-testid="renewal-first-renewal-alert"
          >
            <div className="h-9 w-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-4 h-4 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-amber-100 font-semibold mb-1">
                {t('التجديد الأول — تنبيه عمولة الوكيل', 'First Renewal — Agent Commission Reminder')}
              </p>
              <p className="text-xs text-amber-200/80 leading-relaxed">
                {t(
                  `هذا هو التجديد الأول للشركة، والوكيل "${company.agent_name}" مرتبط بهذا الملف. عند وجود عمولة على تجديد الرخصة يُستحسن تفعيل "عمولة الوكيل" أدناه ليتم إضافتها إلى سجل الوكيل.`,
                  `This is the customer's first renewal, and agent "${company.agent_name}" is linked to this file. When a renewal commission applies, consider enabling "Agent commission" below so it is added to the agent's ledger.`,
                )}
              </p>
            </div>
          </section>
        )}

        {/* iter-279c — Linked agent banner (Task D).
             Shows the current agent snapshot so the operator knows who
             may earn commission on this renewal. Toggling "yes" on the
             agent-commission card below will attach the commission to
             this agent. Empty state prompts the operator to attach an
             agent from the company detail page first. */}
        {company && (company.agent_id || company.agent_name || company.agent_email) && (
          <section
            className="p-4 rounded-2xl bg-amber-500/[0.06] border border-amber-500/25 flex items-start gap-3"
            data-testid="renewal-agent-banner"
          >
            <div className="h-9 w-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
              <User className="h-4 w-4 text-amber-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-200/80 font-semibold mb-1">
                {t('الوكيل المرتبط بالملف', 'Linked agent on this file')}
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-white">
                <span className="font-medium">
                  {company.agent_name || (isAr ? '(بلا اسم)' : '(no name)')}
                </span>
                {company.agent_email && (
                  <span className="text-white/70 font-mono text-xs" dir="ltr">
                    {company.agent_email}
                  </span>
                )}
                {company.agent_phone && (
                  <span className="text-white/70 font-mono text-xs" dir="ltr">
                    {company.agent_phone}
                  </span>
                )}
                {company.agent_id && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300">
                    {t('مسجَّل', 'Registered')}
                  </span>
                )}
              </div>
              <p className="text-[11px] text-amber-200/60 mt-1">
                {t('عند تفعيل "عمولة الوكيل" أدناه سيحصل هذا الوكيل على العمولة على هذا التجديد.',
                   'When "agent commission" is enabled below, this agent will earn the commission on this renewal.')}
              </p>
            </div>
          </section>
        )}
        {company && !(company.agent_id || company.agent_name || company.agent_email) && (
          <section className="p-3 rounded-2xl bg-white/[0.02] border border-dashed border-white/10 text-xs text-white/50">
            {t('لا يوجد وكيل مرتبط بالملف — لن تظهر بيانات وكيل عند تفعيل "عمولة الوكيل".',
               'No agent linked to this file — no agent info will surface if "agent commission" is enabled.')}
          </section>
        )}

        {/* Section 1: License data — iter-280 (Feb 2026) expanded to
             capture the full trade-license snapshot at renewal time
             (mirrors company-registration's TradeLicenseBox). */}
        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-4">
          <h2 className="text-white font-bold text-sm flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 text-white text-xs font-bold flex items-center justify-center">1</span>
            <Building2 className="w-4 h-4 text-emerald-300" />
            {t('بيانات الرخصة التجارية المُجدَّدة', 'Renewed Commercial License Data')}
          </h2>
          <p className="text-[11px] text-white/40 -mt-2">
            {t('أدخل بيانات الرخصة الجديدة كما تظهر على وثيقة التجديد.',
               'Enter the renewed license data as shown on the renewal document.')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label={t('رقم الرخصة الجديد', 'New license #') + ' *'}>
              <Input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-license-number" />
            </Field>
            <Field label={t('الرقم الموحد', 'Unified #')}>
              <Input value={unifiedLicenseNumber} onChange={(e) => setUnifiedLicenseNumber(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-unified-number" />
            </Field>
            <Field label={t('تاريخ الإصدار', 'Issue date') + ' *'}>
              <Input type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-issue-date" />
            </Field>
            <Field label={t('تاريخ الانتهاء', 'Expiry date') + ' *'}>
              <Input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-expiry-date" />
            </Field>
            <Field label={t('اسم الشركة (عربي)', 'Company name (Arabic)')}>
              <Input value={companyNameAr} onChange={(e) => setCompanyNameAr(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white"
                     data-testid="renewal-company-name-ar" />
            </Field>
            <Field label={t('اسم الشركة (إنجليزي)', 'Company name (English)')}>
              <Input value={companyNameEn} onChange={(e) => setCompanyNameEn(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-company-name-en" />
            </Field>
            <Field label={t('جهة الإصدار', 'Issuing authority')}>
              <Input value={issuingAuthority} onChange={(e) => setIssuingAuthority(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white"
                     placeholder={t('DED / RAKEZ / SPC / …', 'DED / RAKEZ / SPC / …')}
                     data-testid="renewal-authority" />
            </Field>
            <Field label={t('نوع الرخصة', 'License type')}>
              <Input value={licenseType} onChange={(e) => setLicenseType(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white"
                     placeholder={t('تجارية / صناعية / مهنية / …', 'Commercial / Industrial / Professional / …')}
                     data-testid="renewal-license-type" />
            </Field>
            <Field label={t('الشكل القانوني', 'Legal form')}>
              <Input value={legalForm} onChange={(e) => setLegalForm(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white"
                     placeholder={t('ذ.م.م / مؤسسة فردية / …', 'LLC / Sole proprietor / …')}
                     data-testid="renewal-legal-form" />
            </Field>
            <Field label={t('رأس المال (AED)', 'Capital (AED)')}>
              <Input type="number" step="0.01" min="0" value={capital}
                     onChange={(e) => setCapital(e.target.value)}
                     className="bg-[#050505] border-white/10 text-white" dir="ltr"
                     data-testid="renewal-capital" />
            </Field>
          </div>
        </section>

        {/* Section 2: Renewal receipts */}
        <ReceiptsBlock
          testIdPrefix="renewal-receipts"
          title={
            <span className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 text-white text-xs font-bold flex items-center justify-center">2</span>
              <IdCard className="w-4 h-4 text-sky-300" />
              {t('رسوم الرخصة (إيصالات دفع التجديد)', 'License Fees (Renewal Payment Receipts)')}
            </span>
          }
          hint={t('كل إيصال يُوسم "مسدد لجهة حكومية" أو "مسدد لنا" — يقوم الذكاء الاصطناعي بالتحقق من المبالغ.',
                  'Each receipt is tagged as paid_to_gov / paid_to_us — AI verifies the amounts.')}
          receipts={renewalReceipts}
          onAdd={(files) => addReceipts(files, 'renewal')}
          onRemove={(idx) => removeReceipt('renewal', idx)}
          onUpdate={(idx, patch) => updateReceipt('renewal', idx, patch)}
          sum={renewalSum}
          verifyResult={renewalVerifyResult}
          onVerify={() => verifyReceipts('renewal')}
          t={t} isAr={isAr}
        />

        {/* Section 3: Commission audit merged card */}
        <section className="bg-purple-500/[0.04] border border-purple-500/20 rounded-2xl p-5 space-y-4"
                 data-testid="renewal-commission-card">
          <h2 className="text-purple-200 font-bold text-sm flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 text-white text-xs font-bold flex items-center justify-center">3</span>
            <Coins className="w-4 h-4" />
            {t('تدقيق العمولات', 'Commission Audit')}
          </h2>

          {/* Toggle A: renewal commission */}
          <div className="p-4 rounded-xl bg-[#050505]/60 border border-white/5 space-y-3">
            <YesNoToggle
              label={t('هل يوجد عمولة على تجديد الرخصة؟', 'Is there a commission on this license renewal?')}
              value={hasRenewalCommission}
              onChange={setHasRenewalCommission}
              testIdBase="renewal-commission-toggle"
              isAr={isAr}
            />
            {hasRenewalCommission && (
              <ReceiptsBlock
                testIdPrefix="renewal-commission-receipts"
                title={t('إيصالات عمولة التجديد', 'Renewal commission receipts')}
                hint={t('ارفع إيصال إيداع العمولة — يجب توسيمه "مسدد لنا" (لبنك CloudGM).',
                        'Upload the commission deposit receipt — must be tagged paid_to_us (CloudGM bank).')}
                receipts={commissionReceipts}
                onAdd={(files) => addReceipts(files, 'commission')}
                onRemove={(idx) => removeReceipt('commission', idx)}
                onUpdate={(idx, patch) => updateReceipt('commission', idx, patch)}
                sum={commissionSum}
                verifyResult={commissionVerifyResult}
                onVerify={() => verifyReceipts('commission')}
                nested
                t={t} isAr={isAr}
              />
            )}
          </div>

          {/* Toggle B: agent commission */}
          <div className="p-4 rounded-xl bg-[#050505]/60 border border-white/5 space-y-3">
            <YesNoToggle
              label={t('هل يوجد عمولة للوكيل؟', 'Is there a commission for the agent?')}
              value={hasAgentCommission}
              onChange={setHasAgentCommission}
              testIdBase="agent-commission-toggle"
              isAr={isAr}
            />
            {hasAgentCommission && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-white/70 text-xs mb-1 block">
                    {t('مبلغ عمولة الوكيل (AED)', 'Agent commission (AED)') + ' *'}
                  </label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={agentCommissionAmount}
                    onChange={(e) => setAgentCommissionAmount(e.target.value)}
                    className="bg-[#050505] border-white/10 text-white" dir="ltr"
                    data-testid="agent-commission-amount"
                  />
                </div>
                {meta?.agent && (
                  <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/20 text-xs">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-3 h-3 text-cyan-400" />
                      <span className="text-cyan-300 font-medium">{t('الوكيل', 'Agent')}</span>
                    </div>
                    <div className="text-white/80">{meta.agent.agent_name || meta.agent.name || '—'}</div>
                    <div className="text-white/50">{meta.agent.agent_email || meta.agent.email || '—'}</div>
                  </div>
                )}
                {isEdit && existing?.agent_commission_stopped_at && (
                  <div className="col-span-full p-3 rounded-lg bg-red-500/5 border border-red-500/20 text-xs text-red-200">
                    {t(`أُوقفت العمولة في ${existing.agent_commission_stopped_at.slice(0, 10)}`,
                       `Commission stopped on ${existing.agent_commission_stopped_at.slice(0, 10)}`)}
                  </div>
                )}
                {isEdit && !existing?.agent_commission_stopped_at && (
                  <Button
                    onClick={stopAgentCommission}
                    variant="outline"
                    className="border-red-500/30 bg-transparent text-red-300 hover:bg-red-500/10 col-span-full"
                    data-testid="agent-commission-stop-btn"
                  >
                    {t('إيقاف منح العمولة للوكيل', 'Stop granting commission to agent')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Section 4: Admin fees */}
        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-3"
                 data-testid="renewal-admin-fees-section">
          <h2 className="text-white font-bold text-sm flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 text-white text-xs font-bold flex items-center justify-center">4</span>
            <Landmark className="w-4 h-4 text-amber-300" />
            {t('الرسوم الإدارية', 'Admin Fees')}
          </h2>
          <YesNoToggle
            label={t('هل تنطبق رسوم إدارية؟', 'Do admin fees apply?')}
            value={adminFeesApply}
            onChange={setAdminFeesApply}
            testIdBase="renewal-admin-fees-toggle"
            isAr={isAr}
          />
          {adminFeesApply && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label={t('المبلغ (AED)', 'Amount (AED)') + ' *'}>
                <Input type="number" step="0.01" min="0"
                       value={adminFeesAmount}
                       onChange={(e) => setAdminFeesAmount(e.target.value)}
                       className="bg-[#050505] border-white/10 text-white" dir="ltr"
                       data-testid="renewal-admin-fees-amount" />
              </Field>
              <Field label={t('وجهة السداد', 'Paid to')}>
                <PaidToPicker value={adminFeesPaidTo} onChange={setAdminFeesPaidTo}
                              testIdBase="renewal-admin-fees-paidto" t={t} />
              </Field>
            </div>
          )}
        </section>

        {/* Section 5: Attachments — iter-280 (Feb 2026) */}
        <section
          className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 space-y-3"
          data-testid="renewal-attachments-section"
        >
          <h2 className="text-white font-bold text-sm flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-fuchsia-500 to-fuchsia-700 text-white text-xs font-bold flex items-center justify-center">5</span>
            <Paperclip className="w-4 h-4 text-fuchsia-300" />
            {t('المرفقات', 'Attachments')}
          </h2>
          <p className="text-[11px] text-white/50">
            {t('صورة الرخصة الجديدة، مستندات موافقة، أو أي ملف داعم آخر.',
               'The renewed license file, approvals, or any other supporting file.')}
          </p>
          <input
            ref={attachRef}
            type="file"
            multiple
            accept="image/*,application/pdf,.doc,.docx"
            className="hidden"
            onChange={(e) => addAttachments(e.target.files)}
            data-testid="renewal-attach-input"
          />
          <button
            type="button"
            onClick={() => attachRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-fuchsia-500/30 hover:border-fuchsia-500/60 text-fuchsia-200 text-sm transition"
            data-testid="renewal-attach-add-btn"
          >
            <Plus className="w-4 h-4" />
            {t('إضافة مرفقات', 'Add attachments')}
          </button>
          {attachments.length > 0 && (
            <ul className="space-y-1.5">
              {attachments.map((a, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-fuchsia-500/[0.06] border border-fuchsia-500/20 text-xs"
                  data-testid={`renewal-attach-row-${i}`}
                >
                  <FileText className="h-3.5 w-3.5 text-fuchsia-300 flex-shrink-0" />
                  <span className="flex-1 truncate text-white/80">{a.label || a.name}</span>
                  <a
                    href={a.data_url}
                    download={a.name}
                    className="px-2 py-1 rounded-md text-fuchsia-200 hover:text-white bg-black/30 hover:bg-black/50 border border-white/10 inline-flex items-center gap-1"
                    data-testid={`renewal-attach-download-${i}`}
                  >
                    <Download className="h-3 w-3" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setAttachments((prev) => prev.filter((_, k) => k !== i))}
                    className="p-1 rounded hover:bg-red-500/10 text-red-400/70"
                    data-testid={`renewal-attach-remove-${i}`}
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Section 6: Notes */}
        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-5">
          <label className="text-white/70 text-xs mb-1 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-500 to-slate-700 text-white text-xs font-bold flex items-center justify-center">6</span>
            {t('ملاحظات', 'Notes')}
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-[#050505] border border-white/10 text-white rounded-md p-3 text-sm mt-2"
            placeholder={t('اختياري', 'Optional')}
            data-testid="renewal-notes"
          />
        </section>

        {/* Submit */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Button variant="outline"
                  onClick={() => navigate(`/manage/cloudgm-agent/companies/${companyId}`)}
                  className="border-white/10 bg-transparent text-white/80 h-12"
                  data-testid="renewal-cancel-btn">
            <ArrowLeft className="w-4 h-4 me-2" />
            {t('إلغاء والعودة', 'Cancel & back')}
          </Button>
          <Button onClick={save} disabled={!canSave || saving}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-12 disabled:opacity-50"
                  data-testid="renewal-save-btn">
            {saving
              ? <><Loader2 className="w-4 h-4 animate-spin me-2" />{t('جارٍ الحفظ…', 'Saving…')}</>
              : <><Save className="w-4 h-4 me-2" />{isEdit ? t('حفظ التعديلات', 'Save changes')
                                                         : t('إنشاء طلب التجديد', 'Create renewal request')}</>}
          </Button>
        </div>
        {!canSave && (
          <div className="text-[11px] text-amber-300 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {t('راجع الحقول: رقم الرخصة الجديد + تاريخ الإصدار + تاريخ الانتهاء + إيصال تجديد واحد على الأقل + تحقق ذكاء اصطناعي على كل الإيصالات',
               'Check fields: new license # + issue date + expiry date + at least one renewal receipt + AI verification on every receipt')}
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-white/70 text-xs mb-1.5">{label}</label>
      {children}
    </div>
  );
}

/**
 * SnapshotField — iter-280 (Feb 2026)
 * Compact read-only value tile used in the "Current license" card.
 */
function SnapshotField({ label, value, mono = false, highlight = false, colSpan = 1 }) {
  const displayVal = value === 0 || value === '0' ? '0' : (value || '—');
  return (
    <div
      className={`rounded-lg bg-black/30 border border-white/5 p-2.5 ${colSpan === 2 ? 'col-span-2' : ''}`}
    >
      <div className="text-[10px] text-white/40 mb-1">{label}</div>
      <div
        className={`truncate text-white ${mono ? 'font-mono' : ''} ${highlight ? 'text-amber-300 font-semibold' : ''}`}
        dir={mono ? 'ltr' : undefined}
      >
        {displayVal}
      </div>
    </div>
  );
}

function YesNoToggle({ label, value, onChange, testIdBase, isAr }) {
  return (
    <div>
      <label className="text-white/80 text-sm mb-2 block font-medium">{label}</label>
      <div className="inline-flex gap-2">
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${
            value === true
              ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200'
              : 'bg-[#050505] border-white/10 text-white/60 hover:border-white/30'
          }`}
          data-testid={`${testIdBase}-yes`}
        >
          {isAr ? 'نعم' : 'Yes'}
        </button>
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-4 py-1.5 rounded-lg text-sm border transition-colors ${
            value === false
              ? 'bg-red-500/15 border-red-500/40 text-red-200'
              : 'bg-[#050505] border-white/10 text-white/60 hover:border-white/30'
          }`}
          data-testid={`${testIdBase}-no`}
        >
          {isAr ? 'لا' : 'No'}
        </button>
      </div>
    </div>
  );
}

function PaidToPicker({ value, onChange, testIdBase, t }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {[
        { id: 'to_gov', ar: 'مسدد لجهة حكومية', en: 'Paid to gov authority', cls: 'text-amber-200 bg-amber-500/10 border-amber-500/40' },
        { id: 'to_us',  ar: 'مسدد لنا',           en: 'Paid to us',            cls: 'text-emerald-200 bg-emerald-500/10 border-emerald-500/40' },
      ].map(o => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`px-3 py-2 rounded-lg border text-xs transition-colors ${
            value === o.id ? o.cls : 'bg-[#050505] border-white/10 text-white/60'
          }`}
          data-testid={`${testIdBase}-${o.id}`}
        >
          {t(o.ar, o.en)}
        </button>
      ))}
    </div>
  );
}

function ReceiptsBlock({
  testIdPrefix, title, hint, receipts, onAdd, onRemove, onUpdate,
  sum, verifyResult, onVerify, nested = false, t, isAr,
}) {
  const fileRef = useRef(null);
  return (
    <section className={nested ? '' : 'bg-white/[0.02] border border-white/10 rounded-2xl p-5'}
             data-testid={`${testIdPrefix}-section`}>
      {!nested && <h2 className="text-white font-bold text-sm mb-2">{title}</h2>}
      {nested && <div className="text-white/80 text-xs mb-2 font-medium">{title}</div>}
      {hint && <p className="text-[11px] text-white/50 mb-3">{hint}</p>}

      <input
        ref={fileRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        onChange={(e) => onAdd(e.target.files)}
        data-testid={`${testIdPrefix}-input`}
      />
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed border-emerald-500/30 hover:border-emerald-500/60 text-emerald-200 text-sm transition"
        data-testid={`${testIdPrefix}-add-btn`}
      >
        <Upload className="w-4 h-4" />
        {t('إضافة إيصال / إيصالات', 'Add receipt(s)')}
      </button>

      {receipts.length > 0 && (
        <ul className="space-y-2 mt-3">
          {receipts.map((r, idx) => (
            <li
              key={r.file_id || idx}
              className={`p-3 rounded-lg border ${r.ai_verified ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-white/[0.02] border-white/10'}`}
              data-testid={`${testIdPrefix}-row-${idx}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <FileImage className={`w-4 h-4 ${r.ai_verified ? 'text-emerald-400' : 'text-white/60'} flex-shrink-0`} />
                <span className="text-xs text-white/90 flex-1 truncate">{r.filename}</span>
                {r.ai_verified && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> {t('تم التحقق', 'Verified')}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="p-1 rounded hover:bg-red-500/10 text-red-400/70"
                  data-testid={`${testIdPrefix}-remove-${idx}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] text-white/50 mb-1 block">{t('المبلغ (AED)', 'Amount (AED)') + ' *'}</label>
                  <Input type="number" step="0.01" min="0"
                         value={r.amount}
                         onChange={(e) => onUpdate(idx, { amount: e.target.value })}
                         className="bg-[#050505] border-white/10 text-white h-9" dir="ltr"
                         data-testid={`${testIdPrefix}-amount-${idx}`} />
                </div>
                <div>
                  <label className="text-[10px] text-white/50 mb-1 block">{t('وجهة السداد', 'Paid to')}</label>
                  <PaidToPicker value={r.paid_to || 'to_us'}
                                onChange={(v) => onUpdate(idx, { paid_to: v })}
                                testIdBase={`${testIdPrefix}-paidto-${idx}`} t={t} />
                </div>
                <div>
                  <label className="text-[10px] text-white/50 mb-1 block">{t('المرجع', 'Reference')}</label>
                  <Input value={r.reference || ''}
                         onChange={(e) => onUpdate(idx, { reference: e.target.value })}
                         className="bg-[#050505] border-white/10 text-white h-9" dir="ltr" />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {receipts.length > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-white/70">{t('إجمالي الإيصالات', 'Receipts sum')}</span>
            <span className="font-mono text-white">{sum.toFixed(2)} AED</span>
          </div>
          <Button
            type="button"
            onClick={onVerify}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white"
            data-testid={`${testIdPrefix}-verify-btn`}
          >
            <Sparkles className="w-4 h-4 me-2" />
            {t('تحقق بالذكاء الاصطناعي', 'AI Verify Receipts')}
          </Button>
          {verifyResult && (
            <div className={`p-3 rounded-lg text-xs border ${verifyResult.ok
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
              : 'bg-red-500/10 border-red-500/30 text-red-200'}`}
              data-testid={`${testIdPrefix}-verify-result`}
            >
              <div className="font-medium mb-1">
                {isAr ? verifyResult.reason_ar : verifyResult.reason_en}
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
